import React from 'react';
import css from './AppearancesFormFields.module.css';
import { FormattedMessage, useIntl } from 'react-intl';

import TitleInputField from '../TitleInputField';
import PriceInputField from '../PriceInputField';
import HoursInputField from '../HoursInputField';
import PersonalizationFormField from '../PersonalizationFormField';
import LocationInputFormField from '../LocationInputFormField/LocationInputFormField';
import BookingTimeFormField from '../BookingTimeFormField/BookingTimeFormField';
import classNames from 'classnames';

const APPEARANCE_CONFIG = {
  'Meet and Greet': {
    needsLocation: true,
    descriptionLabel: 'Describe the Meet and Greet',
    descriptionPlaceholder:
      'e.g. 30-minute meet & greet with fans after the game, photo ops, autographs...',
  },
  'Skill Demonstration': {
    needsLocation: true,
    descriptionLabel: 'Describe the Skill Demonstration',
    descriptionPlaceholder:
      'e.g. Demonstrate your signature crossover move with slow-motion breakdown...',
  },
  'Digital Interview': {
    needsLocation: false,
    descriptionLabel: 'Describe the Digital Interview',
    descriptionPlaceholder:
      'e.g. 20-minute Zoom interview about your career journey and upcoming season...',
  },
  'In-person Interview': {
    needsLocation: true,
    descriptionLabel: 'Describe the In-person Interview',
    descriptionPlaceholder: 'e.g. Sit-down interview at team facility with local news station...',
  },
  Speech: {
    needsLocation: true,
    descriptionLabel: 'Describe the Speech',
    descriptionPlaceholder: 'e.g. 10-minute motivational speech at high school graduation...',
  },
  Podcast: {
    needsLocation: false,
    descriptionLabel: 'Describe the Podcast appearance',
    descriptionPlaceholder:
      'e.g. 45-minute episode on "The Players Lounge" podcast, in-person or remote...',
  },
  'Production Shoot': {
    needsLocation: true,
    descriptionLabel: 'Describe the Production Shoot',
    descriptionPlaceholder: 'e.g. Full-day commercial shoot for sports drink brand...',
  },
  'Autograph Session': {
    needsLocation: true,
    descriptionLabel: 'Describe the Autograph Session',
    descriptionPlaceholder: 'e.g. 2-hour signing at card shop, photos allowed, bring sharpies...',
  },
  'Golf Outing': {
    needsLocation: true,
    descriptionLabel: 'Describe the Golf Outing',
    descriptionPlaceholder: 'e.g. Charity golf tournament, play 18 holes with sponsors...',
  },
  'Watch Party': {
    needsLocation: true,
    descriptionLabel: 'Describe the Watch Party',
    descriptionPlaceholder: 'e.g. Host 50 fans at local bar to watch your game together...',
  },
  'Game Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Game Appearance',
    descriptionPlaceholder: 'e.g. Throw first pitch, halftime recognition, on-field ceremony...',
  },
  'Practice Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Practice Appearance',
    descriptionPlaceholder: 'e.g. Visit youth football practice, run drills, give pep talk...',
  },
  'Camp Participation': {
    needsLocation: true,
    descriptionLabel: 'Describe the Camp',
    descriptionPlaceholder: 'e.g. 3-day youth skills camp, coach stations, give awards...',
  },
  'Virtual Classroom Visit': {
    needsLocation: false,
    descriptionLabel: 'Describe the Virtual Classroom Visit',
    descriptionPlaceholder:
      'e.g. 30-minute Zoom with 5th grade class, answer questions about perseverance...',
  },
  'Fundraising Event Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Fundraising Event',
    descriptionPlaceholder: 'e.g. Gala dinner, auction host, photo ops with donors...',
  },
  'Halftime Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Halftime Appearance',
    descriptionPlaceholder: 'e.g. On-court recognition, shoot free throws with kids...',
  },
  'Media Day Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Media Day Appearance',
    descriptionPlaceholder: 'e.g. Full media availability, interviews, photo shoots...',
  },
  'Esports Event Appearance': {
    needsLocation: true,
    descriptionLabel: 'Describe the Esports Event Appearance',
    descriptionPlaceholder: 'e.g. Guest commentator for tournament finals, meet fans at booth...',
  },
};

const AppearancesFormFields = ({ formId, values, listing }) => {
  const intl = useIntl();
  const selectedTitle = values?.title;
  const config = selectedTitle ? APPEARANCE_CONFIG[selectedTitle] : null;

  // Show only title + price until type is selected
  if (!selectedTitle || !config) {
    return (
      <>
        <div className={css.offeringBlock}>
          <div className={css.offeringHeader}>
            <h4>
              <FormattedMessage id="AppearancesFormFields.sectionHeading" />
            </h4>
          </div>
        </div>

        <TitleInputField label="Type" id="title" fieldName="title" />
        <PriceInputField formId={formId} />
      </>
    );
  }

  const {
    needsLocation,
    descriptionLabel,
    descriptionPlaceholder = 'Provide as much detail as possible about the appearance...',
  } = config;

  const eventName = selectedTitle;

  return (
    <>
      <div className={css.offeringBlock}>
        <div className={css.offeringHeader}>
          <h4>
            <FormattedMessage id="AppearancesFormFields.sectionHeading" />
          </h4>
        </div>
      </div>
      <div className={css.formRow}>
        <div className={css.formFld}>
          {/* Title */}
          <TitleInputField label="Title" id="title" fieldName="title" />
        </div>
        <div className={css.formFld}>
          {/* Price */}
          <PriceInputField formId={formId} />
        </div>
      </div>

      <div className={classNames(css.formRow, css.fullWidth)}>
        <div className={css.formFld}>
          {/* Hours */}
          <HoursInputField
            label="How many hours?"
            fieldName="hours"
            id="hours"
            placeholder="e.g. 2 hours"
          />
        </div>
      </div>

      {/* Booking Time */}
      <BookingTimeFormField listing={listing} />

      {/* Description */}
      <PersonalizationFormField
        label={descriptionLabel}
        placeholder={descriptionPlaceholder}
        fieldName="personalizationText"
      />

      {/* Location - Only if needed */}
      {needsLocation && (
        <LocationInputFormField
          fieldName="location"
          label="Location"
          placeholder="Enter venue name, address, or city"
        />
      )}

      {/* Optional Note */}
      {!needsLocation && selectedTitle === 'Virtual Classroom Visit' && (
        <div className={css.infoNote}>
          <p>
            <strong>
              <FormattedMessage id="CreativeMediaFormFields.virtualAppearanceNotePrefix" />
            </strong>{' '}
            <FormattedMessage id="CreativeMediaFormFields.virtualAppearanceNoteText" />
          </p>
        </div>
      )}
    </>
  );
};

export default AppearancesFormFields;
